import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Grid, Tab, Tabs, TextField, Typography } from '@mui/material';
import DataDisplay from '../../components/DataDisplay';
import { API } from '../../api/api_setup';
import { useAuth } from '../../hooks/useAuth';
import CustomTabPanel from '../CustomTabPanel';
import tokens from '../../styles/tokens';


const ResourceUtilizationPanel = ({ clusterName }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [nodeMetrics, setNodeMetrics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [nodeFilter, setNodeFilter] = useState('All');
  const [nodeOptions, setNodeOptions] = useState(['All']);

  const formatNameValue = (name, node) => {
    const podName = name && name !== 'NA' ? name : (name || 'NA');
    const nodeSuffix = node && node !== 'NA' ? ` (${node})` : '';
    return `${podName}${nodeSuffix}`;
  };

  const formatPercent = (value) => {
    if (!value || value === 'NA') return 'NA';
    const str = String(value).trim();
    return str.endsWith('%') ? str : `${str}%`;
  };

  const formatCpuCapacity = (value) => {
    if (!value || value === 'NA') return 'NA';
    return `${value} cores`;
  };

  const formatMemoryCapacity = (value) => {
    if (!value || value === 'NA') return 'NA';
    const str = String(value).trim();
    const match = str.replace(/,/g, '').match(/^([\d.]+)\s*(ki|kb|mi|mb|gi|gb|ti|tb)?/i);
    if (!match) return str;
    const num = Number(match[1]);
    if (Number.isNaN(num)) return str;
    const unit = (match[2] || '').toLowerCase();
    let giValue;
    switch (unit) {
      case 'ki':
      case 'kb':
        giValue = num / (1024 * 1024);
        break;
      case 'mi':
      case 'mb':
        giValue = num / 1024;
        break;
      case 'ti':
      case 'tb':
        giValue = num * 1024;
        break;
      case 'gi':
      case 'gb':
        giValue = num;
        break;
      default:
        return str;
    }
    return `${giValue.toFixed(1)} Gi`;
  };

  const detailColumns = [
    {
      field: 'displayName',
      headerName: 'Name',
      width: 320,
      valueGetter: (params) => params?.row?.displayName || 'NA',
      renderCell: (params) => params?.row?.displayName || 'NA',
      sortComparator: (v1 = '', v2 = '') => String(v1).localeCompare(String(v2)),
    },
    { field: 'cpu_requests', headerName: 'CPU Requests', width: 160 },
    { field: 'cpu_limits', headerName: 'CPU Limits', width: 150 },
    { field: 'memory_requests', headerName: 'Memory Requests', width: 190 },
    { field: 'memory_limits', headerName: 'Memory Limits', width: 180 },
  ];

  const nodeColumns = [
    { field: 'nodeName', headerName: 'Node Name', width: 220 },
    { field: 'status', headerName: 'Status', width: 140 },
    { field: 'cpuUsageDisplay', headerName: 'CPU Usage (%)', width: 170 },
    { field: 'cpuCapacityDisplay', headerName: 'CPU Capacity', width: 170 },
    { field: 'memoryUsageDisplay', headerName: 'Memory Usage (%)', width: 180 },
    { field: 'memoryCapacityDisplay', headerName: 'Memory Capacity', width: 190 },
  ];

  useEffect(() => {
    setIsLoading(true);
    const requestHeaders = { Authorization: `Bearer ${user.token}` };
    API.get(`/cluster_debug/resource_utilization/${clusterName}?limit=500`, { headers: requestHeaders })
      .then((response) => {
        const payload = response?.data?.data || {};
        const sanitizedOverall = (payload.overall_metrics || []).map((row) => {
          const normalizedName = row?.name || 'NA'
          const normalizedNode = row?.node || 'NA'
          return {
            ...row,
            name: normalizedName,
            node: normalizedNode,
            displayName: formatNameValue(normalizedName, normalizedNode),
            cpu_requests: row?.cpu_requests || 'NA',
            cpu_limits: row?.cpu_limits || 'NA',
            memory_requests: row?.memory_requests || 'NA',
            memory_limits: row?.memory_limits || 'NA',
          }
        });
        const sanitizedNodes = (payload.node_usage || []).map((row, idx) => {
          const nodeName = row?.['Node Name'] || row?.nodeName || 'NA'
          const cpuUsageRaw = row?.['CPU Usage (%)'] || row?.cpuUsage || 'NA'
          const memoryUsageRaw = row?.['Memory Usage (%)'] || row?.memoryUsage || 'NA'
          const cpuCapacityRaw = row?.cpu_capacity || row?.cpuCapacity || 'NA'
          const memoryCapacityRaw = row?.memory_capacity || row?.memoryCapacity || 'NA'
          return {
            id: idx + 1,
            nodeName,
            status: row?.Status || row?.status || 'NA',
            cpuUsageRaw,
            cpuUsageDisplay: formatPercent(cpuUsageRaw),
            cpuCapacityRaw,
            cpuCapacityDisplay: formatCpuCapacity(cpuCapacityRaw),
            memoryUsageRaw,
            memoryUsageDisplay: formatPercent(memoryUsageRaw),
            memoryCapacityRaw,
            memoryCapacityDisplay: formatMemoryCapacity(memoryCapacityRaw),
          }
        });
        setRows(sanitizedOverall);
        setNodeMetrics(sanitizedNodes);
        const nodeSet = new Set();
        sanitizedOverall.forEach((row) => {
          if (row?.node) nodeSet.add(row.node);
        });
        sanitizedNodes.forEach((row) => {
          if (row?.nodeName && row.nodeName !== 'NA') nodeSet.add(row.nodeName);
        });
        const options = ['All', ...Array.from(nodeSet).sort()];
        setNodeOptions(options);
      })
      .catch(() => { })
      .finally(() => setIsLoading(false));
  }, [clusterName, user.token]);

  const filteredOverall = nodeFilter === 'All'
    ? rows
    : rows.filter((row) => row?.node === nodeFilter);

  const filteredNodeMetrics = nodeFilter === 'All'
    ? nodeMetrics
    : nodeMetrics.filter((row) => row?.nodeName === nodeFilter);

  const tabConfig = [
    {
      label: 'Overall Metrics',
      component: (
        <DataDisplay
          rows={filteredNodeMetrics}
          columns={nodeColumns}
          loading={isLoading}
          sx={{ margin: '10px', marginTop: '0px' }}
          density='compact'
          noRowsText='No node utilization data found'
        />
      )
    },
    {
      label: 'Detailed Metrics',
      component: (
        <DataDisplay
          rows={filteredOverall}
          columns={detailColumns}
          loading={isLoading}
          sx={{ margin: '10px', marginTop: '0px' }}
          density='compact'
          noRowsText='No resource utilization data found'
        />
      )
    }
  ];

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Grid container alignItems={'center'}>
        <Grid item sx={{ paddingLeft: '10px', paddingRight: '20px' }}>
          <Typography variant='h6' color={'#313638'}>{'Resource Utilization'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ padding: '10px' }}>
          <Autocomplete
            options={nodeOptions}
            value={nodeFilter}
            onChange={(e, newValue) => setNodeFilter(newValue || 'All')}
            renderInput={(params) => <TextField {...params} label="Node" size='small' />}
            filterSelectedOptions
            disableClearable
          />
        </Grid>
      </Grid>
      <Grid item lg={12} md={12} sx={{ paddingBottom: '16px' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="resource utilization tabs"
            variant='scrollable'
            TabIndicatorProps={{ style: { height: 3, borderRadius: 2, background: tokens.colors.primary } }}
          >
            {tabConfig.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 800,
                  color: tokens.colors.textSecondary,
                  '&.Mui-selected': { color: tokens.colors.primary },
                  minHeight: 44
                }}
                id={`simple-tab-${index}`}
                aria-controls={`simple-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>
        {tabConfig.map((tab, index) => (
          <CustomTabPanel
            key={tab.label}
            value={tabValue}
            index={index}
            sx={{ padding: 0 }}
          >
            {tab.component}
          </CustomTabPanel>
        ))}
      </Grid>
    </>
  );
};

export default ResourceUtilizationPanel;
